<template>
  <div class="">
    <div v-if="supplierOrder" class="flex flex-col">
      <h3 class="capitalize-first text-2xl font-bold text-gray-700">
        {{
          supplierOrder.id
            ? supplierOrder?.recipient?.name
            : $t('global.new_supplier_order')
        }}
      </h3>
      <div class="col-span-2">
        <div class="flex justify-end">
          <StatusInput
            v-model="localSupplierOrder.status"
            :options="options"
            :disabled="localSupplierOrder.status === Status.DRAFT"
          />
        </div>
      </div>
      <div class="grid-form">
        <div class="col-span-2">
          <SelectSupplier
            v-model="localSupplierOrder.recipient_id"
            :edition-mode="localSupplierOrder.status === Status.DRAFT"
            searchable
            error-key="recipient_id"
            :errors="errors?.recipient_id"
          />
        </div>
        <div class="col-span-2">
          <SelectCompany
            v-model="localSupplierOrder.company_id"
            name="company_id"
            edition-mode
            required
            :label="$t('attributes.company')"
            :disabled="
              !!localSupplierOrder.company_id &&
              (localSupplierOrder.reference || hasAttachedItems)
            "
            :errors="errors?.company_id"
            error-key="company_id"
          />
        </div>
        <div class="col-span-2">
          <SelectFuneralHome
            v-model="localSupplierOrder.funeral_home_id"
            name="funeral_home_id"
            edition-mode
            required
            :filter-company-id="localSupplierOrder.company_id"
            :disabled="
              !!localSupplierOrder.funeral_home_id &&
              (localSupplierOrder.reference || hasAttachedItems)
            "
            :errors="errors?.funeral_home_id"
            error-key="funeral_home_id"
          />
        </div>
        <div class="col-span-2">
          <PlaceInput
            v-model:place-model="localSupplierOrder.delivery_place"
            v-model:place-id="localSupplierOrder.delivery_place_id"
            v-model:place-type="localSupplierOrder.delivery_place_kind"
            :available-place-type="[
              PlaceType.FUNERARIUM,
              PlaceType.SALLE_FUNERARIUM,
              PlaceType.EGLISE,
              PlaceType.CIMETIERE,
              PlaceType.CREMATORIUM,
              PlaceType.WIKI_FUNERARIUM,
              PlaceType.WIKI_SALLE_CEREMONIE,
              PlaceType.WIKI_SALLE_DE_RECEPTION
            ]"
            :default-place-type="PlaceType.SALLE_FUNERARIUM"
            default-place-group="company_place"
            :label="$t('attributes.place')"
            name="delivery_place"
            edition-mode
          />
        </div>
        <div v-if="!localSupplierOrder.delivery_place_kind" class="col-span-2">
          <BaseShowEditInput
            v-model="localSupplierOrder.delivery_place_other"
            :label="$t('attributes.place')"
          />
        </div>
        <div v-if="!localSupplierOrder.delivery_place_kind" class="col-span-2">
          <BaseShowEditAddress
            v-model:country="localSupplierOrder.delivery_place_country"
            v-model:zip="localSupplierOrder.delivery_place_zip"
            v-model:city="localSupplierOrder.delivery_place_city"
            v-model:street="localSupplierOrder.delivery_place_street"
            v-model:extra-street="
              localSupplierOrder.delivery_place_extra_street
            "
            v-model:city-id="localSupplierOrder.delivery_place_city_id"
            v-model:current-city-object="
              localSupplierOrder.delivery_place_city_object
            "
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditDateTimePicker
            v-model:date="localSupplierOrder.delivery_date_time"
            v-model:time-undefined="
              localSupplierOrder.delivery_date_time_undefined_time
            "
            :label-date="$t('global.estimate_delivery_date')"
            name="delivery_date_time"
            :edition-mode="true"
            :errors="errors?.delivery_date_time"
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditTextArea
            v-model="localSupplierOrder.delivery_remarks"
            class="col-span-2 col-start-1"
            name="delivery_remarks"
            :label="$t('attributes.delivery_remarks')"
            :errors="errors.delivery_remarks"
            :edition-mode="true"
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditTextArea
            v-model="localSupplierOrder.remarks"
            class="col-span-2 col-start-1"
            name="remarks"
            :label="$t('attributes.remarks')"
            :errors="errors.remarks"
            :edition-mode="true"
          />
        </div>
        <div class="col-span-2">
          <BaseShowEditTextArea
            v-model="localSupplierOrder.note_intern"
            class="col-span-2 col-start-1"
            :label="$t('attributes.internal_note')"
            name="note_intern"
            :errors="errors.note_intern"
            :edition-mode="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { DocTypes, InvoiceableType, Status } from '@/assets/enums'
import { supplierOrderHasItemDossiers } from '@u/helpers/stockOriginalHelpers'
import BaseShowEditDateTimePicker from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import StatusInput from '@c/addf-package/components/status/StatusInput.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import PlaceInput from '@c/addf-package/components/Place/PlaceInput.vue'
import { PlaceType } from '@/assets/enums/index.js'
import SelectSupplier from '@c/InitialStateFormComponent/SelectSupplier.vue'
import SelectCompany from '@c/InitialStateFormComponent/SelectCompany.vue'
import SelectFuneralHome from '@c/InitialStateFormComponent/SelectFuneralHome.vue'
import BaseShowEditAddress from '@c/BaseShowEditAddress.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'

export default {
  name: 'SupplierOrderForm',
  components: {
    BaseShowEditInput,
    BaseShowEditAddress,
    SelectFuneralHome,
    SelectCompany,
    SelectSupplier,
    PlaceInput,
    BaseShowEditTextArea,
    StatusInput,
    BaseShowEditDateTimePicker
  },
  props: {
    supplierOrder: {
      type: Object,
      required: false,
      default: () => ({})
    },
    accountDiaries: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['switch-supplier-order', 'update:localSupplierOrder'],
  data() {
    return {
      InvoiceableType: InvoiceableType,
      DocTypes: DocTypes,
      Status: Status,
      selectedSupplierOrderItem: null,
      selectedLocation: null
    }
  },
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata',
      errors: 'auth/getErrors'
    }),
    PlaceType() {
      return PlaceType
    },
    localSupplierOrder: {
      get() {
        return this.supplierOrder
      },
      set(value) {
        this.$emit('update:localSupplierOrder', value)
      }
    },
    options() {
      const statuses = this.initialMetadata?.enums?.supplier_order_status ?? []

      if (!this.supplierOrder.id || this.supplierOrder.status === Status.DRAFT)
        return statuses.filter((status) => [Status.DRAFT].includes(status.id))

      if (!this.supplierOrder.reference)
        return statuses.filter((status) =>
          ['REC', 'LIT', 'PAR_REC', 'SEN', 'EST'].includes(status.id)
        )

      return statuses.filter((status) =>
        ['REC', 'LIT', 'PAR_REC', 'SEN', 'CONF'].includes(status.id)
      )
    },
    hasAttachedItems() {
      return supplierOrderHasItemDossiers(this.supplierOrder)
    }
  },
  watch: {
    supplierOrder: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal && !newVal.id) {
          newVal.status = Status.DRAFT
          this.$emit('update:localSupplierOrder', newVal)
        }
      }
    }
  }
}
</script>
